import { SynthesiseRecipe, SynthesiseRecipeInput } from '../../../../src/commonTypes';

export const SYNTHESISE_INPUT_ROLES = [
	['primary-concept', 'Primary concept'],
	['brief', 'Brief'],
	['audience', 'Audience'],
	['motivation', 'Motivation'],
	['brand-voice', 'Brand voice'],
	['brand-colours', 'Brand colours'],
	['evidence', 'Evidence'],
	['visual-reference', 'Visual reference'],
	['constraint', 'Constraint'],
	['supporting-concept', 'Supporting concept'],
] as const;

export type SynthesiseInputRole = (typeof SYNTHESISE_INPUT_ROLES)[number][0];

type SynthesiseSlot =
	| 'subject'
	| 'audience'
	| 'motivation'
	| 'brand-voice'
	| 'brand-colours'
	| 'visual-reference'
	| 'evidence'
	| 'constraint'
	| 'supporting-concept';

const ROLE_TO_SLOT: Record<SynthesiseInputRole, SynthesiseSlot> = {
	'primary-concept': 'subject',
	brief: 'subject',
	audience: 'audience',
	motivation: 'motivation',
	'brand-voice': 'brand-voice',
	'brand-colours': 'brand-colours',
	evidence: 'evidence',
	'visual-reference': 'visual-reference',
	constraint: 'constraint',
	'supporting-concept': 'supporting-concept',
};

const SLOT_HEADING: Record<SynthesiseSlot, string> = {
	subject: 'Subject',
	audience: 'Target audience',
	motivation: 'Motivation',
	'brand-voice': 'Brand voice',
	'brand-colours': 'Brand colours',
	'visual-reference': 'Visual reference',
	evidence: 'Evidence',
	constraint: 'Constraint',
	'supporting-concept': 'Supporting concept',
};

const AUDIENCE_IS_TARGET_TASKS = new Set([
	'Post',
	'Brief',
	'Tagline',
	'Strategy',
	'TechnicalSpec',
	'Prod-ideas',
	'Audience',
]);

const TEXT_SLOT_USAGE: Record<SynthesiseSlot, string> = {
	subject: 'This is what to create or develop.',
	audience:
		'This is who the output is FOR. Write to match their world. Do not greet, address, or message them by name. Do not start with "Hi {name}".',
	motivation: 'Use this to explain why the subject matters to the audience.',
	'brand-voice': 'Match this voice and tone.',
	'brand-colours':
		'Visual identity only. Do not dump hex codes into copy unless the task is about visual design.',
	'visual-reference': 'Optional visual context. Do not describe this as the written output.',
	evidence: 'Ground claims in this evidence. Do not invent sources.',
	constraint: 'Honour these limits.',
	'supporting-concept': 'Additional context for the subject.',
};

const SOCIAL_POST_TRANSFORMATION_RULE =
	'Treat the supplied concepts, solutions and briefs as source material for the post, not as an outline or output format to reproduce. Select one compelling angle and transform it into platform-native writing. Do not turn the source into a toolkit, specification, feature inventory, numbered process or checklist unless the user explicitly requests that format.';

const SOCIAL_POST_PLATFORM_RULES: Record<string, string> = {
	LinkedIn:
		'Write an authentic professional LinkedIn feed post with a clear human point of view. Open with an observation, tension, experience or useful insight. Develop one idea through 2-4 short paragraphs and close naturally with a reflection, question or relevant call to action. Do not use headings, numbered steps or a checklist unless explicitly requested. Use bullets only when genuinely necessary, with no more than three. Avoid generic motivational language and invented personal experience. Use up to 3 hashtags only when they add discoverability.',
	Instagram:
		'Write an expressive, conversational Instagram caption. When a visual is supplied, write a caption that complements the visual instead of restating or explaining it. Begin with a vivid, emotional or curiosity-building line; add a concise micro-story or relatable observation; then invite one simple interaction. Use natural line breaks. Do not produce numbered instructions unless the requested format is a carousel or tutorial. Use relevant searchable language and a small number of genuinely useful hashtags.',
	X:
		'Write one natural, conversational X post within 280 characters. Express one observation, reaction, question or timely idea and get to the point immediately. Do not compress a multi-step guide into the post. Use a call to action only when applicable and no more than 2 relevant hashtags.',
	// Retained for recipes saved before Reddit was removed from the current UI.
	Reddit:
		'Write a specific descriptive title and a candid, substantive body that invites genuine discussion. Avoid marketing language, promotional calls to action and hashtags. Do not turn the source into a checklist unless the user explicitly asks for one.',
};

const IMAGE_SLOT_USAGE: Record<SynthesiseSlot, string> = {
	subject: 'Depict this as the main scene or object.',
	audience:
		'Who the image is for. Do not paint their name on the image. Only depict them for a portrait task.',
	motivation: 'Optional mood or need to express visually.',
	'brand-voice': 'Optional mood or styling notes.',
	'brand-colours': 'Use these exact colours as the dominant palette. Honour every named hex value.',
	'visual-reference': 'Use as a style or composition reference.',
	evidence: 'Ignore unless it describes a visible object.',
	constraint: 'Honour these visual limits.',
	'supporting-concept': 'Include as supporting visual context.',
};

const BRAND_PALETTE_BLOCK_START = 'BRAND PALETTE (MANDATORY)';
const BRAND_PALETTE_BLOCK_END = 'END BRAND PALETTE';

export type CompiledSynthesisePrompt = {
	textPrompt: string;
	imagePrompt: string;
	imageUrl?: string;
};

export const synthesiseRoleLabel = (role: string): string =>
	SYNTHESISE_INPUT_ROLES.find(([value]) => value === role)?.[1] || 'Context';

const normalizeSearchable = (value: string): string =>
	value.toLowerCase().replace(/[-_]+/g, ' ');

export const inferSynthesiseInputRole = ({
	type,
	label,
	src,
	isPrimary = false,
}: {
	type?: string;
	label?: string;
	src?: string;
	isPrimary?: boolean;
}): SynthesiseInputRole => {
	const searchable = normalizeSearchable(`${type || ''} ${label || ''}`);
	const typeTokens = normalizeSearchable(type || '').split(/\s+/).filter(Boolean);
	if (/\bpersona\b/.test(searchable) || typeTokens[0] === 'audience' || type === 'audience') {
		return 'audience';
	}
	if (/\bmotivation/.test(searchable)) return 'motivation';
	if (searchable.includes('brand strategy') || searchable.includes('tagline') || searchable.includes('brand voice')) {
		return 'brand-voice';
	}
	if (searchable.includes('brand colour') || searchable.includes('brand color')) {
		return 'brand-colours';
	}
	if (src || type?.startsWith('image') || type === 'search-image') return 'visual-reference';
	if (
		type === 'article' ||
		type === 'search-page' ||
		searchable.includes('search') ||
		searchable.includes('source web') ||
		searchable.includes('source pdf')
	) {
		return 'evidence';
	}
	if (searchable.includes('brief') || searchable.includes('specification')) return 'brief';
	return isPrimary ? 'primary-concept' : 'supporting-concept';
};

const slotForRole = (role: string): SynthesiseSlot =>
	ROLE_TO_SLOT[role as SynthesiseInputRole] || 'supporting-concept';

const inputBody = (input: SynthesiseRecipeInput): string => {
	const value = (input.value || '').trim();
	const label = (input.label || '').trim();
	if (!value) return label;
	if (label && !value.toLocaleLowerCase().includes(label.toLocaleLowerCase())) {
		return `${label}\n${value}`;
	}
	return value;
};

type PaletteColour = { name?: string; hex?: string; role?: string };

const formatPaletteContent = (text: string): string => {
	try {
		const parsed = JSON.parse(text) as {
			name?: string;
			rationale?: string;
			colours?: PaletteColour[];
		};
		if (Array.isArray(parsed.colours) && parsed.colours.length > 0) {
			const colours = parsed.colours
				.map((colour) =>
					[colour.role, colour.name, colour.hex?.toUpperCase()].filter(Boolean).join(': ')
				)
				.join('; ');
			return [parsed.name?.trim(), parsed.rationale?.trim(), colours].filter(Boolean).join('\n');
		}
	} catch {
		// Board cards store palette as labelled text, not JSON.
	}
	return text.trim();
};

const groupedSlots = (inputs: SynthesiseRecipeInput[]): Array<[SynthesiseSlot, SynthesiseRecipeInput[]]> => {
	const groups = new Map<SynthesiseSlot, SynthesiseRecipeInput[]>();
	const order: SynthesiseSlot[] = [
		'subject',
		'audience',
		'motivation',
		'brand-voice',
		'brand-colours',
		'constraint',
		'evidence',
		'supporting-concept',
		'visual-reference',
	];
	for (const input of inputs) {
		const slot = slotForRole(input.role);
		const existing = groups.get(slot) || [];
		existing.push(input);
		groups.set(slot, existing);
	}
	return order
		.filter((slot) => groups.has(slot))
		.map((slot) => [slot, groups.get(slot)!]);
};

const usesImagePrompt = (task: string): boolean =>
	task.startsWith('Image') || task.startsWith('Video');

const isPortraitTask = (task: string): boolean => task === 'Image-Portrait';

const audienceUsageForTask = (task: string): string => {
	if (AUDIENCE_IS_TARGET_TASKS.has(task) || task.startsWith('Image') || task.startsWith('Video')) {
		return TEXT_SLOT_USAGE.audience;
	}
	return 'Use this person or group as context. Do not open by greeting them by name.';
};

const compileTextPrompt = (
	recipe: Pick<SynthesiseRecipe, 'task' | 'inputs' | 'instructions' | 'settings'>
): string => {
	const sections: string[] = [
		`Task: ${recipe.task}`,
		'Use each section only for the purpose stated after the heading. Do not mix roles.',
	];
	if (AUDIENCE_IS_TARGET_TASKS.has(recipe.task)) {
		sections.push(
			'If a target audience or persona is supplied, they are who this is for — not the person being spoken to.'
		);
	}
	if (recipe.task === 'Post') {
		const platform = recipe.settings?.platform || 'LinkedIn';
		sections.push(
			`## Social writing rule\n${SOCIAL_POST_TRANSFORMATION_RULE}`,
			`## Social platform\nPlatform: ${platform}. ${SOCIAL_POST_PLATFORM_RULES[platform] || SOCIAL_POST_PLATFORM_RULES.LinkedIn}`,
			'Output only the finished, publish-ready social post. Do not output planning notes, an outline, labels such as “Hook” or “CTA”, or commentary about the post.'
		);
	}

	for (const [slot, items] of groupedSlots(recipe.inputs)) {
		const bodies = items
			.map((item) => (slot === 'brand-colours' ? formatPaletteContent(inputBody(item)) : inputBody(item)))
			.filter(Boolean);
		if (bodies.length === 0) continue;
		const usage =
			slot === 'audience'
				? audienceUsageForTask(recipe.task)
				: slot === 'subject' && recipe.task === 'Post'
					? 'This is source material and the topic for the post. Write about its strongest idea; do not build the proposed solution or reproduce the source structure.'
				: slot === 'visual-reference' && recipe.task === 'Post'
					? 'The image is attached to this request. Let the post complement it rather than narrating the obvious. Use only concrete visual cues that are actually visible, and do not invent visual details.'
					: TEXT_SLOT_USAGE[slot];
		sections.push(`## ${SLOT_HEADING[slot]}\n${usage}\n${bodies.join('\n\n')}`);
	}

	if (recipe.instructions?.trim()) {
		sections.push(`## Additional instructions\n${recipe.instructions.trim()}`);
	}
	const preferences = [recipe.settings?.tone, recipe.settings?.length, recipe.settings?.format].filter(
		Boolean
	);
	if (preferences.length > 0) {
		sections.push(`## Output preferences\n${preferences.join('. ')}.`);
	}
	return sections.join('\n\n');
};

const compileImagePrompt = (
	recipe: Pick<SynthesiseRecipe, 'task' | 'inputs' | 'instructions'>
): { prompt: string; imageUrl?: string } => {
	const groups = new Map(groupedSlots(recipe.inputs));
	const parts: string[] = [];
	// Media providers are inclined to turn a broad brief into a generic
	// illustration. Make a user-specified deliverable (logo, hero, product
	// shot, social asset, infographic, portrait, etc.) a hard constraint. The
	// same compiled prompt is used for image and video tasks.
	parts.push(
		'OUTPUT-FORM RULE: If the brief specifies an output form or channel (for example logo, social post, infographic, product image, hero image, portrait, campaign asset, or video), create that exact deliverable. Do not substitute a generic illustration, poster, or unrelated format. Preserve the requested subject, composition, and format before adding style.'
	);

	const scene = [...(groups.get('subject') || []), ...(groups.get('supporting-concept') || [])]
		.map(inputBody)
		.filter(Boolean)
		.join('. ');
	if (scene) parts.push(scene);

	const palette = (groups.get('brand-colours') || [])
		.map((item) => formatPaletteContent(inputBody(item)))
		.filter(Boolean)
		.join('; ');
	if (palette) {
		parts.push(
			`${BRAND_PALETTE_BLOCK_START}\n${IMAGE_SLOT_USAGE['brand-colours']} ${palette}\n${BRAND_PALETTE_BLOCK_END}`
		);
	}

	const audience = (groups.get('audience') || []).map(inputBody).filter(Boolean).join('\n');
	if (audience) {
		parts.push(
			isPortraitTask(recipe.task)
				? `Portrait of this person (keep the stated name and gender exactly): ${audience}`
				: `${IMAGE_SLOT_USAGE.audience} ${audience}`
		);
	}

	for (const slot of ['motivation', 'brand-voice', 'constraint'] as SynthesiseSlot[]) {
		const body = (groups.get(slot) || []).map(inputBody).filter(Boolean).join('\n');
		if (!body) continue;
		parts.push(`${IMAGE_SLOT_USAGE[slot]} ${body}`);
	}

	if (recipe.instructions?.trim()) {
		parts.push(recipe.instructions.trim());
	}

	const visual = (groups.get('visual-reference') || []).find((item) => item.src);
	const portraitSource =
		isPortraitTask(recipe.task) && !visual
			? (groups.get('audience') || []).find((item) => item.src)
			: undefined;

	return {
		prompt: parts.join('\n'),
		imageUrl: visual?.src || portraitSource?.src,
	};
};

export const compileSynthesisePrompt = (
	recipe: Pick<SynthesiseRecipe, 'task' | 'inputs' | 'instructions' | 'settings'>
): CompiledSynthesisePrompt => {
	const image = compileImagePrompt(recipe);
	return {
		textPrompt: compileTextPrompt(recipe),
		imagePrompt: image.prompt || compileTextPrompt(recipe),
		imageUrl: image.imageUrl,
	};
};

export const promptForSynthesiseTask = (
	compiled: CompiledSynthesisePrompt,
	task: string
): string => (usesImagePrompt(task) ? compiled.imagePrompt : compiled.textPrompt);
